import '../Sass/App.scss';
import React from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';

const README_TERM_PATTERN = /\b(functions?|listeners?|specials?|programs?|procedures?)\b/gi;
const README_DOCUMENTS = [
    { fileName: 'README.rules.md', page: 'rules' },
    { fileName: 'README.devguide.md', page: 'devguide' }
];

function formatReadmeText(text) {
    if (typeof text !== 'string') {
        return text;
    }

    const parts = [];
    let cursor = 0;

    for (const match of text.matchAll(README_TERM_PATTERN)) {
        const matchIndex = match.index;
        const precedingText = text.slice(cursor, matchIndex);
        const isViralTerm = /\bviral\s+$/i.test(text.slice(0, matchIndex));

        parts.push(precedingText);

        if (isViralTerm) {
            parts.push(match[0]);
        } else {
            const term = match[0].toLowerCase().replace(/s$/, '');
            parts.push(
                <span className={`readme-term readme-term-${term}`} key={`${matchIndex}-${term}`}>
                    {match[0]}
                </span>
            );
        }

        cursor = matchIndex + match[0].length;
    }

    parts.push(text.slice(cursor));
    return parts;
}

function formatReadmeListItem(item) {
    if (typeof item !== 'string') {
        return item;
    }

    const colonIndex = item.indexOf(':');

    if (colonIndex <= 0) {
        return formatReadmeText(item);
    }

    const label = item.slice(0, colonIndex);
    const details = item.slice(colonIndex);

    return (
        <>
            <strong>{formatReadmeText(label)}</strong>
            {formatReadmeText(details)}
        </>
    );
}

function sectionId(section) {
    return String(section.id || section.title || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

function runAction(action, changePage) {
    if (action.onClick) {
        action.onClick();
        return;
    }

    if (action.page && changePage) {
        changePage(action.page, action.sendId || 0);
    }
}

function renderActionButtons(actions, changePage, key = 'actions') {
    return (
        <div className="readme-actions" key={key}>
            {actions.map((action) => (
                <button className="home-command" onClick={() => runAction(action, changePage)} key={action.label}>
                    <span>&gt;</span> {action.label}
                </button>
            ))}
        </div>
    );
}

function renderBlock(block, index, changePage) {
    if (typeof block === 'string') {
        return <p key={index}>{formatReadmeText(block)}</p>;
    }

    if (block.type === 'list') {
        return (
            <ul className="readme-list" key={index}>
                {block.items.map((item) => <li key={item}>{formatReadmeListItem(item)}</li>)}
            </ul>
        );
    }

    if (block.type === 'code') {
        return (
            <pre className="readme-code" key={index}>
                <code>{block.text}</code>
            </pre>
        );
    }

    if (block.type === 'callout') {
        return (
            <div className={`readme-callout readme-callout-${block.tone || 'info'}`} key={index}>
                {block.label && <div className="readme-callout-label">{formatReadmeText(block.label)}</div>}
                <div>{formatReadmeText(block.text)}</div>
            </div>
        );
    }

    if (block.type === 'image') {
        return (
            <figure className="readme-image" key={index}>
                {block.label && <div className="readme-image-label">{block.label}</div>}
                <img src={block.src} alt={block.alt || block.caption || ''} />
                {block.caption && <figcaption>{formatReadmeText(block.caption)}</figcaption>}
            </figure>
        );
    }

    if (block.type === 'actions') {
        return renderActionButtons(block.actions || [], changePage, index);
    }

    return null;
}

export function ReadmePage({ title, subtitle, fileName, version, status, sections = [], actions = [], changePage }) {
    return (
        <Container className="main-content readme-page" maxWidth="xl">
            <Grid container spacing={3}>
                <Grid xs={12} md={3}>
                    <aside className="readme-sidebar">
                        <div className="readme-file-tree">
                            <div className="green-code">/docs</div>
                            {README_DOCUMENTS.map((document) => (
                                <button
                                    className={document.fileName === fileName ? 'readme-file-link readme-file-active' : 'readme-file-link'}
                                    disabled={document.fileName === fileName}
                                    key={document.fileName}
                                    onClick={() => changePage && changePage(document.page, 0)}
                                    type="button"
                                >
                                    {document.fileName}
                                </button>
                            ))}
                        </div>
                        <nav className="readme-toc" aria-label={`${title} table of contents`}>
                            <div className="green-code">contents</div>
                            {sections.map((section) => (
                                <a href={`#${sectionId(section)}`} key={sectionId(section)}>
                                    {section.title}
                                </a>
                            ))}
                        </nav>
                    </aside>
                </Grid>
                <Grid xs={12} md={9}>
                    <article className="readme-document">
                        <header className="readme-header">
                            <div className="readme-terminal-bar">
                                <span className="green-code">{fileName}</span>
                                <span className="grey-code">{status || 'draft'}</span>
                            </div>
                            <h1>{title}</h1>
                            {subtitle && <p>{formatReadmeText(subtitle)}</p>}
                            {version && <div className="readme-version">version {version}</div>}
                            {actions.length > 0 && renderActionButtons(actions, changePage)}
                        </header>
                        {sections.map((section) => (
                            <section className="readme-section" id={sectionId(section)} key={sectionId(section)}>
                                <h2>{section.title}</h2>
                                {(section.blocks || []).map((block, index) => renderBlock(block, index, changePage))}
                            </section>
                        ))}
                    </article>
                </Grid>
            </Grid>
        </Container>
    );
}
